import { crewOnDay } from '../personnel/rest-hours.js';
import type { CrewRestReport } from '../personnel/types.js';

export interface MlcSnapshot {
  /** Değerlerin günü: raporun günü ya da (gün sürüyorsa) ölçülmüş son gün. */
  date: string;
  /** Gemideki herkesin o günkü en kötü durumu. */
  status: 'compliant' | 'warning' | 'violation' | 'incomplete';
  /** En az dinlenen kişi; değerler onun. */
  userId: string;
  rest24h: number | null;
  rest7d: number | null;
  work24h: number;
  work7d: number;
  /** [MLC A2.3 §5(a)]: 24 saatte 10, 7 günde 77 saat dinlenme. */
  limits: { rest24h: number; rest7d: number };
}

const RANK = { compliant: 0, incomplete: 1, warning: 2, violation: 3 } as const;

/**
 * Noon Report'un MLC kartı ve Quick Summary'si (ADR 0022 §6): raporun gününde gemideki bütün
 * personelin uyumu. Karar (ADR 0024 #15): gemi geneli, en kötü durum görünür; sayılar
 * o gün en az dinlenen kişinin. Kaydı eksik kişi uyumlu sayılmaz. `members`: rapor gününde bu
 * gemide görevli olanlar (ortak notu P1, 2026-11-02; `membersOn`); rapor aralığa değen herkesi
 * verir, dün ayrılan kişi kartı düşürmez. `null` süzgeç yok (üyelik okunamadı).
 */
export function mlcSnapshot(
  report: readonly CrewRestReport[],
  reportDay: string,
  members: ReadonlySet<string> | null = null,
): MlcSnapshot | null {
  const crew = crewOnDay(report, members);
  // Gün sürüyorsa 24 saatlik dinlenme henüz ölçülmez: rapor gününe kadar ölçülmüş son gün
  const measuredDays = crew
    .flatMap((member) => member.days)
    .filter((d) => d.date <= reportDay && d.rest24h !== null)
    .map((d) => d.date)
    .sort();
  const date = measuredDays.includes(reportDay) ? reportDay : (measuredDays.at(-1) ?? reportDay);
  const rows = crew.flatMap((member) => {
    const index = member.days.findIndex((d) => d.date === date);
    const today = member.days[index];
    if (!today) return [];
    // 7 günde çalışma + dinlenme = 168 saat; dinlenme ölçülmediyse elimizdeki günlerin toplamı
    const week = member.days.slice(Math.max(0, index - 6), index + 1);
    const work7d =
      today.rest7d !== null ? 168 - today.rest7d : week.reduce((sum, d) => sum + d.workHours, 0);
    return [{ member, today, work7d }];
  });
  if (rows.length === 0) return null;
  const status = rows.reduce<MlcSnapshot['status']>(
    (worst, row) => (RANK[row.today.status] > RANK[worst] ? row.today.status : worst),
    'compliant',
  );
  const measured = rows.filter((row) => row.today.rest24h !== null);
  const worst = (measured.length > 0 ? measured : rows).reduce((a, b) =>
    (b.today.rest24h ?? Number.POSITIVE_INFINITY) < (a.today.rest24h ?? Number.POSITIVE_INFINITY)
      ? b
      : a,
  );
  return {
    date,
    status,
    userId: worst.member.userId,
    rest24h: worst.today.rest24h,
    rest7d: worst.today.rest7d,
    work24h: worst.today.workHours,
    work7d: worst.work7d,
    limits: { rest24h: 10, rest7d: 77 },
  };
}
